import React from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import AgTechFadeInView from '../../Pages/Loading/AgTechFadeInView';
import { useAgTechWebAppContext } from './AgTechWebAppContext';
import { isDev } from '@agtech/core/utilities/AgTechUtilities';

const AgTechWebErrorBoundary = (props: { children?: React.ReactElement }) => {
    const errorHandler = useAgTechErrorHandler();

    return (
        <ErrorBoundary
            // onError={errorHandler.handleError}
            FallbackComponent={AgTechWebErrorPage}
        >
            {props.children}
        </ErrorBoundary>
    )
};

export const AgTechLocalWebErrorBoundary = (props: { 
    children?: React.ReactNode,
    onError?: (error: Error) => Promise<void>
}) => {
    const errorHandler = useAgTechErrorHandler();

    const handleError = async (error: Error) => {
        await props.onError?.(error);
        errorHandler.handleError(error);
    }

    return (
        <ErrorBoundary
            onError={handleError}
            FallbackComponent={AgTechMinorWebErrorPage}
        >
            {props.children}
        </ErrorBoundary>
    )
}

export const useAgTechErrorHandler = () => {
    const appContext = useAgTechWebAppContext();

    return {
        handleError: (error: Error) => {
            if (appContext && isDev()) {
                appContext.toasts.showError({
                    header: error.message,
                    details: error.stack
                })
            }
        }
    }
}

const AgTechWebErrorPage = () => {
    // The error boundary renders outside of the web app context, so it should not make use of any clickable components such
    // as buttons or links. This is because the error boundary is a last resort for handling errors and should not be relied
    // upon to be able to handle any user interactions.
    return (
        <AgTechFadeInView classes='stretched row-based centered bg-primary font-color-white container'>
            <i className={`fas fa-exclamation bg-red-400 text-white mb-4 h-[32px] w-[32px] rounded-[50%] centered flex`} />
            <h1 className='text-xl font-semibold mb-2' >We're Sorry!</h1>
            <h2 className='text-md mb-10'>An error occurred within the application.</h2>
            <h3 className='text-base mb-6'>To restart, please click the button below. If the issue persists, please contact us.</h3>
            <button
                className='button-highlight py-2-half px-6 font-size-large'
                onClick={() => {
                    window.location.href = '/app/home';
                }}
            >
                Restart Application
            </button>
        </AgTechFadeInView>
    )
}

const AgTechMinorWebErrorPage = () => {
    // The error boundary renders outside of the web app context, so it should not make use of any clickable components such
    // as buttons or links. This is because the error boundary is a last resort for handling errors and should not be relied
    // upon to be able to handle any user interactions.
    return (
        <AgTechFadeInView classes='stretched row-based centered bg-white min-w-[320px] min-h-[180px] flex'>
            <i className={`fas fa-exclamation centered h-[32px] w-[32px] rounded-[50%] bg-red font-color-white mb-2 flex`} />
            <h1 className='text-base font-semibold'>An error occurred.</h1>
        </AgTechFadeInView>
    )
}

export default React.memo(AgTechWebErrorBoundary);